import { Control, useFormContext } from 'react-hook-form';
import {
    ApplyNowFormValues,
    IApplyQuestion,
    IApplyNowAdditionalQuestionsForm,
    IDateRangeAnswer,
    QuestionAnswerFields,
    QuestionType,
    UploadFileTypes,
} from '../../../../../common';
import {
    Box,
    FormInputText,
    FormDatePicker,
    FormUploadFile,
    FormTextArea,
    FormRadioButtonGroup,
    FormCheckboxGroup,
    FormDateRangePicker,
    Grid,
    Typography,
    useTheme,
    Icon,
    Tooltip,
} from '../../../../../base';
import { useTranslation } from 'react-i18next';
import {
    StyledFileDisplayContainer,
    StyledFileLink,
} from '../JobQuestions.styles';
import moment from 'moment';

interface QuestionAnswerFieldProps {
    question: IApplyQuestion;
    index: number;
    control: Control<IApplyNowAdditionalQuestionsForm>;
    disabled?: boolean;
    showDealbreakerInfo?: boolean;
}

const getBandOptions = (question: IApplyQuestion) =>
    (question.options || []).filter((opt) => opt.rangeInd);

const getChoiceOptions = (question: IApplyQuestion) =>
    (question.options || []).filter((opt) => !opt.rangeInd);

export const QuestionAnswerField = ({
    question,
    index,
    control,
    disabled = false,
    showDealbreakerInfo = false,
}: QuestionAnswerFieldProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { getValues } = useFormContext<IApplyNowAdditionalQuestionsForm>();

    const fieldPath =
        `${ApplyNowFormValues.questionAnswers}.${index}.${QuestionAnswerFields.ANSWER}` as const;

    const currentAnswer = getValues(fieldPath);

    const getExpectedAnswer = (): string => {
        if (question.dealbreakerExpectedAnswer) {
            return question.dealbreakerExpectedAnswer;
        }
        if (question.dealbreakerExpectedOptionValues?.length) {
            const labels = question.dealbreakerExpectedOptionValues.map(
                (value) => {
                    const option = question.options.find(
                        (opt) => String(opt.value) === value
                    );
                    return option?.label || value;
                }
            );
            return labels.join(', ');
        }

        const bands = getBandOptions(question);
        if (!bands.length) return '';

        return bands
            .map((band) => {
                if (band.rangeFrom && band.rangeTo) {
                    return `${band.rangeFrom} - ${band.rangeTo}`;
                }
                if (band.rangeFrom) return `Min: ${band.rangeFrom}`;
                if (band.rangeTo) return `Max: ${band.rangeTo}`;
                return '';
            })
            .filter(Boolean)
            .join(', ');
    };

    const isAnswerMatchingDealbreaker = (): boolean | null => {
        if (question.dealbreakerExpectedAnswer) {
            if (!currentAnswer) return false;
            return (
                String(currentAnswer).toLowerCase() ===
                String(question.dealbreakerExpectedAnswer).toLowerCase()
            );
        }

        if (question.dealbreakerExpectedOptionValues?.length) {
            if (!currentAnswer) return false;
            if (Array.isArray(currentAnswer)) {
                return question.dealbreakerExpectedOptionValues.every((val) =>
                    (currentAnswer as string[]).includes(val)
                );
            }
            return question.dealbreakerExpectedOptionValues.includes(
                String(currentAnswer)
            );
        }

        const bands = getBandOptions(question);
        if (!bands.length) return null;

        if (question.type === QuestionType.NUMERIC) {
            const numAnswer = Number(currentAnswer);
            if (currentAnswer === '' || currentAnswer == null || isNaN(numAnswer)) {
                return false;
            }
            return bands.some((band) => {
                const min = band.rangeFrom ? Number(band.rangeFrom) : -Infinity;
                const max = band.rangeTo ? Number(band.rangeTo) : Infinity;
                return numAnswer >= min && numAnswer <= max;
            });
        }

        if (question.type === QuestionType.SELECT_DATE) {
            if (!currentAnswer) return false;
            const answerDate = moment(currentAnswer as string | Date);
            if (!answerDate.isValid()) return false;
            return bands.some((band) => {
                const start = band.rangeFrom ? moment(band.rangeFrom) : null;
                const end = band.rangeTo ? moment(band.rangeTo) : null;
                if (!start?.isValid() || !end?.isValid()) return false;
                return (
                    answerDate.isSameOrAfter(start, 'day') &&
                    answerDate.isSameOrBefore(end, 'day')
                );
            });
        }

        if (question.type === QuestionType.DATE_RANGE) {
            const range = currentAnswer as IDateRangeAnswer | null;
            if (!range?.from || !range?.to) return false;
            const from = moment(range.from);
            const to = moment(range.to);
            if (!from.isValid() || !to.isValid()) return false;
            return bands.some((band) => {
                const start = band.rangeFrom ? moment(band.rangeFrom) : null;
                const end = band.rangeTo ? moment(band.rangeTo) : null;
                if (!start?.isValid() || !end?.isValid()) return false;
                return (
                    from.isSameOrAfter(start, 'day') &&
                    to.isSameOrBefore(end, 'day')
                );
            });
        }

        return null;
    };

    const shouldShowDealbreakerIcon =
        showDealbreakerInfo && question.isDealbreaker;
    const expectedAnswer = getExpectedAnswer();
    const isMatching = isAnswerMatchingDealbreaker();
    const choiceOptions = getChoiceOptions(question);

    const renderInput = () => {
        switch (question.type) {
            case QuestionType.SHORT_TEXT:
                return (
                    <FormInputText
                        name={fieldPath}
                        control={control}
                        placeholder={t('applyNow.enterAnswer')}
                        disabled={disabled}
                        size="small"
                    />
                );

            case QuestionType.LONG_TEXT:
                return (
                    <FormTextArea
                        name={fieldPath}
                        control={control}
                        placeholder={t('applyNow.enterAnswer')}
                        rows={4}
                        disabled={disabled}
                    />
                );

            case QuestionType.NUMERIC:
                return (
                    <FormInputText
                        name={fieldPath}
                        control={control}
                        placeholder={t('applyNow.enterNumber')}
                        inputType="number"
                        disabled={disabled}
                        size="small"
                    />
                );

            case QuestionType.BOOLEAN:
                return (
                    <FormRadioButtonGroup
                        name={fieldPath}
                        control={control}
                        disabled={disabled}
                        buttonsContent={[
                            { key: 'true', label: t('common.true') },
                            { key: 'false', label: t('common.false') },
                        ]}
                        direction="row"
                    />
                );

            case QuestionType.SINGLE_SELECT:
                return (
                    <FormRadioButtonGroup
                        name={fieldPath}
                        control={control}
                        disabled={disabled}
                        buttonsContent={choiceOptions.map((opt) => ({
                            key: String(opt.value),
                            label: opt.label,
                        }))}
                        direction="row"
                    />
                );

            case QuestionType.MULTIPLE_SELECT:
                return (
                    <FormCheckboxGroup
                        name={fieldPath}
                        control={control}
                        options={choiceOptions.map((opt) => ({
                            key: String(opt.value),
                            label: opt.label,
                        }))}
                        disabled={disabled}
                        direction="row"
                    />
                );

            case QuestionType.SELECT_DATE:
                return (
                    <FormDatePicker
                        name={fieldPath}
                        control={control}
                        placeholder={t('applyNow.selectDate')}
                        disabled={disabled}
                        size="small"
                    />
                );

            case QuestionType.DATE_RANGE:
                return (
                    <FormDateRangePicker
                        name={fieldPath}
                        control={control}
                        fromLabel={t('applyNow.fromDate')}
                        toLabel={t('applyNow.toDate')}
                        disabled={disabled}
                        size="small"
                    />
                );

            case QuestionType.FILE_UPLOAD: {
                const fileValue = getValues(fieldPath) as {
                    fileUrl?: string;
                    fileName?: string;
                } | null;

                if (disabled && fileValue?.fileUrl) {
                    return (
                        <StyledFileDisplayContainer>
                            <Icon
                                name="PaperFilledIcon"
                                color="primary"
                            />
                            <StyledFileLink
                                href={fileValue.fileUrl}
                                target="_blank"
                                download
                            >
                                {fileValue.fileName || t('common.file')}
                            </StyledFileLink>
                        </StyledFileDisplayContainer>
                    );
                }

                return (
                    <FormUploadFile
                        name={fieldPath}
                        type={UploadFileTypes.file}
                        disabled={disabled}
                    />
                );
            }

            default:
                return null;
        }
    };

    const dealbreakerIconColor =
        isMatching === null
            ? theme.palette.warning.main
            : isMatching
              ? theme.palette.success.main
              : theme.palette.warning.main;

    return (
        <Box
            sx={{
                py: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Grid container mb={1} justifyContent={'space-between'}>
                <Grid item display="flex">
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                        }}
                    >
                        {question.question}
                    </Typography>
                    {question.isRequired && (
                        <Icon
                            name="AsteriskIcon"
                            size={14}
                            color="secondary"
                            style={{
                                marginInlineStart: 2,
                            }}
                        />
                    )}
                </Grid>
                <Grid item>
                    {shouldShowDealbreakerIcon && (
                        <Tooltip
                            title={
                                <>
                                    {t('jobApplicationPage.requiredAnswer')}
                                    {expectedAnswer ? (
                                        <>
                                            <br />
                                            {expectedAnswer}
                                        </>
                                    ) : null}
                                </>
                            }
                            placement="top"
                        >
                            <Icon
                                name="ErrorOutlineIcon"
                                style={{
                                    height: 20,
                                    width: 20,
                                    color: dealbreakerIconColor,
                                    marginInlineStart: 8,
                                    cursor: 'pointer',
                                }}
                            />
                        </Tooltip>
                    )}
                </Grid>
            </Grid>
            {renderInput()}
        </Box>
    );
};
